import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'
import { lumi } from '../lib/lumi'

interface ShippingAddress {
  recipientName: string
  phone: string
  city: string
  district: string
  address: string
  email: string
}

interface ConvenienceStore {
  storeId: string
  storeName: string
  storeAddress: string
}

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { items, total, clearCart } = useCart()
  const [currentLang] = useState('zh')
  
  // 表單狀態
  const [shippingMethod, setShippingMethod] = useState<'home' | 'store' | 'pickup'>('home')
  const [paymentMethod, setPaymentMethod] = useState('credit_card')
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    recipientName: '',
    phone: '',
    city: '',
    district: '',
    address: '',
    email: ''
  })
  const [convenienceStore, setConvenienceStore] = useState<ConvenienceStore | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [pointsToUse, setPointsToUse] = useState(0)
  const [note, setNote] = useState('')
  
  // UI 狀態
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [userPoints, setUserPoints] = useState(0)
  const [siteSettings, setSiteSettings] = useState<any>(null)
  
  // 計算
  const shippingFee = shippingMethod === 'home' ? 100 : shippingMethod === 'store' ? 60 : 0
  const pointsDiscount = pointsToUse * 1 // 1點 = 1元
  const finalTotal = Math.max(0, total + shippingFee - pointsDiscount)

  // 載入用戶資料和網站設定
  useEffect(() => {
    const user = lumi.auth.user

    const loadData = async () => {
      try {
        // 載入用戶點數（僅登入用戶）
        if (user) {
          const customerResult = await lumi.entities.customers.list({
            filter: { userId: user.userId }
          })
          if (customerResult.list.length > 0) {
            setUserPoints(customerResult.list[0].walletPoints || 0)
            
            // 自動填充用戶資訊
            const customer = customerResult.list[0]
            setShippingAddress(prev => ({
              ...prev,
              recipientName: customer.name || '',
              phone: customer.phone || '',
              email: customer.email || ''
            }))
          }
        }

        // 載入網站設定
        const settingsResult = await lumi.entities.site_settings.list()
        if (settingsResult.list.length > 0) {
          setSiteSettings(settingsResult.list[0])
        }
      } catch (err) {
        console.error('Failed to load data:', err)
      }
    }

    loadData()
  }, [currentLang, navigate])

  // 檢查購物車是否為空
  useEffect(() => {
    if (items.length === 0) {
      navigate(`/${currentLang}/cart`)
    }
  }, [items, currentLang, navigate])

  // 打開超商選擇
  const openStoreSelector = () => {
    const user = lumi.auth.user
    const userId = user?.userId || `guest_${Date.now()}`

    const ezShipUrl = `https://map.ezship.com.tw/ezship_map_web.jsp?suID=${siteSettings?.ezShipSuId || ''}&rtURL=${encodeURIComponent('https://api.lumi.new/v1/functions/p373839333138763776/ezshipCallback')}&webPara=${userId}`
    window.open(ezShipUrl, 'ezship', 'width=800,height=600')
  }

  // 監聽超商選擇回調
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      console.log('📨 收到 postMessage:', event.data)
      
      if (event.data?.type === 'ezship_store_selected') {
        const ezShipData = event.data.data
        console.log('✅ ezShip 門市資料:', ezShipData)
        
        // 轉換為 CheckoutPage 使用的格式
        setConvenienceStore({
          storeId: ezShipData.stCode,
          storeName: ezShipData.stName,
          storeAddress: ezShipData.stAddr
        })
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // 從 localStorage 恢復選中的門市（頁面刷新後）
  useEffect(() => {
    try {
      const stored = localStorage.getItem('ezship_selected_store')
      if (stored) {
        const ezShipData = JSON.parse(stored)
        console.log('💾 從 localStorage 恢復門市:', ezShipData)
        
        setConvenienceStore({
          storeId: ezShipData.stCode,
          storeName: ezShipData.stName,
          storeAddress: ezShipData.stAddr
        })
      }
    } catch (err) {
      console.error('恢復門市資料失敗:', err)
    }
  }, [])

  // 提交訂單
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const user = lumi.auth.user

    // 驗證
    if (shippingMethod === 'home') {
      if (!shippingAddress.recipientName || !shippingAddress.phone || 
          !shippingAddress.city || !shippingAddress.address || !shippingAddress.email) {
        setError('請填寫完整收件資訊')
        setLoading(false)
        return
      }
    } else if (shippingMethod === 'store') {
      if (!convenienceStore) {
        setError('請選擇超商門市')
        setLoading(false)
        return
      }
      if (!shippingAddress.recipientName || !shippingAddress.phone || !shippingAddress.email) {
        setError('超商取貨請填寫收件人姓名、電話及Email')
        setLoading(false)
        return
      }
    } else if (shippingMethod === 'pickup') {
      if (!shippingAddress.recipientName || !shippingAddress.phone || !shippingAddress.email) {
        setError('門市自取請填寫取件人姓名、電話及Email')
        setLoading(false)
        return
      }
    }

    try {
      // 建立訂單
      const orderData = {
        userId: user?.userId || 'guest',
        customerId: user?.userId || null,
        customerName: shippingAddress.recipientName,
        customerEmail: shippingAddress.email,
        orderNumber: `ORD${Date.now()}`,
        items: items.map(item => ({
          productId: item._id,
          productName: item.name,
          productNameEn: item.nameEn || '',
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        subtotal: total,
        shippingFee,
        pointsUsed: pointsToUse,
        pointsDiscount,
        totalAmount: finalTotal,
        shippingMethod,
        shippingAddress: shippingMethod === 'home' ? {
          recipient: shippingAddress.recipientName,
          phone: shippingAddress.phone,
          fullAddress: `${shippingAddress.city}${shippingAddress.district}${shippingAddress.address}`,
          city: shippingAddress.city,
          district: shippingAddress.district
        } : (shippingMethod === 'store' ? {
          recipient: shippingAddress.recipientName,
          phone: shippingAddress.phone,
          fullAddress: '',
          storeName: convenienceStore?.storeName || '',
          storeAddress: convenienceStore?.storeAddress || ''
        } : {
          recipient: shippingAddress.recipientName,
          phone: shippingAddress.phone,
          fullAddress: 'ONGU Studio 信義店 - 台北市信義區信義路四段389號1樓'
        }),
        convenienceStore: shippingMethod === 'store' ? convenienceStore : null,
        paymentMethod: 'credit_card',
        paymentStatus: 'pending',
        status: 'pending',
        notes: note,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: user?.userId || 'guest'
      }

      const order = await lumi.entities.orders.create(orderData)

      // 扣除點數（僅登入用戶）
      if (pointsToUse > 0 && user?.userId) {
        const customerResult = await lumi.entities.customers.list({
          filter: { userId: user.userId }
        })
        if (customerResult.list.length > 0) {
          const customer = customerResult.list[0]
          await lumi.entities.customers.update(customer._id, {
            walletPoints: (customer.walletPoints || 0) - pointsToUse
          })

          // 記錄點數交易
          await lumi.entities.points_transactions.create({
            userId: user.userId,
            orderId: order._id,
            points: -pointsToUse,
            type: 'used',
            description: `訂單 ${orderData.orderNumber} 使用點數`,
            createdAt: new Date().toISOString(),
            creator: user.userId
          })
        }
      }

      // 發起付款
      if (paymentMethod === 'credit_card') {
        // 藍新金流
        const paymentResponse = await fetch('https://api.lumi.new/v1/functions/p373839333138763776/createNewebpayPayment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderNumber: orderData.orderNumber,
            amount: finalTotal,
            email: shippingAddress.email,
            itemDesc: items.map(i => i.name).join(', '),
            returnUrl: `https://api.lumi.new/v1/functions/p373839333138763776/newebpayReturnUrl`,
            notifyUrl: `https://api.lumi.new/v1/functions/p373839333138763776/newebpayCallback`
          })
        })

        if (!paymentResponse.ok) {
          const errorData = await paymentResponse.json()
          throw new Error(errorData.error || '付款初始化失敗')
        }

        const paymentData = await paymentResponse.json()
        
        // 清空購物車
        await clearCart()
        
        // 清空門市選擇記錄（避免下次結帳時保留舊資料）
        localStorage.removeItem('ezship_selected_store')
        
        // 跳轉到付款頁
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = paymentData.paymentUrl
        
        const fields = {
          MerchantID: paymentData.MerchantID,
          TradeInfo: paymentData.TradeInfo,
          TradeSha: paymentData.TradeSha,
          Version: paymentData.Version
        }
        
        Object.entries(fields).forEach(([key, value]) => {
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = key
          input.value = value as string
          form.appendChild(input)
        })
        
        document.body.appendChild(form)
        form.submit()
      }
    } catch (err: any) {
      console.error('Order creation failed:', err)
      setError(err.message || '訂單建立失敗，請稍後再試')
      setLoading(false)
    }
  }

  if (items.length === 0) {
    return null
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      <div className="container mx-auto px-4 py-12">
        <h1 className="text-3xl font-extralight mb-8 text-black dark:text-white">結帳</h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 左側：表單 */}
          <div className="lg:col-span-2 space-y-8">
            {/* 配送方式 */}
            <div className="border border-gray-200 dark:border-gray-800 p-6">
              <h2 className="text-xl font-light mb-4">配送方式</h2>
              <div className="space-y-3">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="shipping"
                    value="home"
                    checked={shippingMethod === 'home'}
                    onChange={(e) => setShippingMethod(e.target.value as 'home')}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">宅配 (NT$ 100)</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="shipping"
                    value="store"
                    checked={shippingMethod === 'store'}
                    onChange={(e) => setShippingMethod(e.target.value as 'store')}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">超商取貨 (NT$ 60)</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="shipping"
                    value="pickup"
                    checked={shippingMethod === 'pickup'}
                    onChange={(e) => setShippingMethod(e.target.value as 'pickup')}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">門市自取 (免運費)</span>
                </label>
              </div>
            </div>

            {/* 收件資訊 */}
            {shippingMethod === 'home' ? (
              <div className="border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-light mb-4">收件資訊</h2>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="收件人姓名*"
                    value={shippingAddress.recipientName}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, recipientName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="聯絡電話*"
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email*"
                    value={shippingAddress.email}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="縣市*"
                      value={shippingAddress.city}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, city: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                      required
                    />
                    <input
                      type="text"
                      placeholder="區域"
                      value={shippingAddress.district}
                      onChange={(e) => setShippingAddress(prev => ({ ...prev, district: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="詳細地址*"
                    value={shippingAddress.address}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                </div>
              </div>
            ) : shippingMethod === 'store' ? (
              <div className="border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-light mb-4">超商取貨</h2>
                
                <div className="space-y-4">
                  {/* 收件人基本資訊 */}
                  <input
                    type="text"
                    placeholder="收件人姓名*"
                    value={shippingAddress.recipientName}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, recipientName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="聯絡電話*"
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email*"
                    value={shippingAddress.email}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  
                  {/* 門市選擇 */}
                  {convenienceStore ? (
                    <div className="space-y-3">
                      {/* 已選門市資訊卡 */}
                      <div className="border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">已選擇門市</span>
                        </div>
                        <p className="text-base font-medium text-black dark:text-white mb-2">
                          {convenienceStore.storeName}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          {convenienceStore.storeAddress}
                        </p>
                      </div>
                      
                      {/* 重新選擇按鈕 */}
                      <button
                        type="button"
                        onClick={openStoreSelector}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                      >
                        重新選擇門市
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={openStoreSelector}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-900"
                    >
                      選擇超商門市
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-light mb-4">門市自取資訊</h2>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="取件人姓名*"
                    value={shippingAddress.recipientName}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, recipientName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <input
                    type="tel"
                    placeholder="聯絡電話*"
                    value={shippingAddress.phone}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email*"
                    value={shippingAddress.email}
                    onChange={(e) => setShippingAddress(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                    required
                  />
                  <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
                    <p className="text-sm font-light mb-2">取件門市資訊</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">ONGU Studio 信義店</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">台北市信義區信義路四段389號1樓</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">營業時間：週一至五 09:00–17:00｜週六 10:00–17:00｜週日公休</p>
                  </div>
                </div>
              </div>
            )}

            {/* 點數折扣（僅會員） */}
            {lumi.auth.user && (
              <div className="border border-gray-200 dark:border-gray-800 p-6">
                <h2 className="text-xl font-light mb-4">點數折扣</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  可用點數: {userPoints} 點
                </p>
                <input
                  type="number"
                  min="0"
                  max={userPoints}
                  value={pointsToUse}
                  onChange={(e) => setPointsToUse(Math.min(Number(e.target.value), userPoints))}
                  placeholder="使用點數"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm"
                />
              </div>
            )}

            {/* 備註 */}
            <div className="border border-gray-200 dark:border-gray-800 p-6">
              <h2 className="text-xl font-light mb-4">訂單備註</h2>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="有什麼需要告訴我們的嗎？"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-black text-sm resize-none"
              />
            </div>
          </div>

          {/* 右側：訂單摘要 */}
          <div className="lg:col-span-1">
            <div className="border border-gray-200 dark:border-gray-800 p-6 sticky top-4">
              <h2 className="text-xl font-light mb-6">訂單摘要</h2>
              
              {/* 商品列表 */}
              <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
                {items.map((item) => (
                  <div key={item._id} className="flex gap-3">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-light truncate">{item.name}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        NT$ {item.price} × {item.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* 金額明細 */}
              <div className="border-t border-gray-200 dark:border-gray-800 pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">小計</span>
                  <span>NT$ {total}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">運費</span>
                  <span>NT$ {shippingFee}</span>
                </div>
                {pointsToUse > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>點數折扣</span>
                    <span>- NT$ {pointsDiscount}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-light border-t border-gray-200 dark:border-gray-800 pt-2 mt-2">
                  <span>總計</span>
                  <span>NT$ {finalTotal}</span>
                </div>
              </div>

              {/* 錯誤訊息 */}
              {error && (
                <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}

              {/* 提交按鈕 */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-6 bg-black dark:bg-white text-white dark:text-black py-3 text-sm font-light hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '處理中...' : '確認付款'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CheckoutPage
